import { useCallback, useEffect, useState } from "react";
import { Alert, Group, Loader, Stack, Text } from "@mantine/core";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { IconCheck } from "@tabler/icons-react";
import { FormattedMessage, useIntl } from "react-intl";

export interface ProgressItem {
  label: string;
  done: boolean;
}

interface CollectionProgressBannerProps {
  variant: "inProgress" | "complete";
  items?: ProgressItem[];
  onDismiss?: () => void;
}

const STORAGE_PREFIX = "poaOverview.collectionBannerDismissed.";

const readDismissed = (storageKey: string | null) => {
  if (!storageKey) {
    return false;
  }

  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    // Storage access throws in private mode; the banner must still render.
    return false;
  }
};

// Dismissal is per device, and keyed on the data it acknowledged, so a later batch of
// pension data brings the banner back instead of staying silently dismissed forever.
export function useDismissed(key: string | null): [boolean, () => void] {
  const storageKey = key ? `${STORAGE_PREFIX}${key}` : null;
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  useEffect(() => {
    setDismissed(readDismissed(storageKey));
  }, [storageKey]);

  const dismiss = useCallback(() => {
    setDismissed(true);

    if (!storageKey) {
      return;
    }

    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // Storage access throws in private mode; dismissing still works for this render.
    }
  }, [storageKey]);

  return [dismissed, dismiss];
}

const alertVars = {
  "--alert-bg": "var(--wallet-color-masterbrand-accent-yellow)",
  "--alert-color": "var(--wallet-color-neutrals-black)",
};

// Mantine pins the body slot to its own black, which the root colour cannot reach.
const alertStyles = {
  message: { color: "var(--wallet-color-neutrals-black)" },
};

export function CollectionProgressBanner({
  variant,
  items,
  onDismiss,
}: CollectionProgressBannerProps) {
  const intl = useIntl();

  if (variant === "complete") {
    return (
      <Alert
        role="status"
        c="var(--wallet-color-neutrals-black)"
        __vars={alertVars}
        styles={alertStyles}
        icon={<CheckBadgeIcon width="20px" aria-hidden="true" />}
        withCloseButton
        onClose={onDismiss}
        closeButtonLabel={intl.formatMessage({
          id: "app.poaOnb.addIncome.dismiss",
          defaultMessage: "Stäng",
        })}
        title={
          <FormattedMessage
            id="app.poaOnb.overview.complete.headline"
            defaultMessage="Allt är hämtat!"
          />
        }
      >
        <FormattedMessage
          id="app.poaOnb.overview.complete.description"
          defaultMessage="Vi har samlat in all pensionsdata och din överblick är nu uppdaterad."
        />
      </Alert>
    );
  }

  return (
    <Alert
      role="status"
      c="var(--wallet-color-neutrals-black)"
      __vars={alertVars}
      styles={alertStyles}
      title={
        <FormattedMessage
          id="app.poaOnb.waiting.headline"
          defaultMessage="Vi är igång!"
        />
      }
    >
      <Stack gap="sm">
        <Text size="sm" lh="sm">
          <FormattedMessage
            id="app.poaOverview.progressBanner.inProgressBody"
            defaultMessage="Vi har redan hämtat delar av din pension och fortsätter nu med resten. Vissa uppgifter kan ta upp till några dagar att hämta."
          />
        </Text>
        {items && items.length > 0 && (
          <Stack gap="xs">
            {items.map((item) => (
              <Group key={item.label} gap="xs" wrap="nowrap">
                {item.done ? (
                  <IconCheck size={16} aria-hidden="true" />
                ) : (
                  <Loader
                    size={14}
                    color="var(--wallet-color-neutrals-black)"
                    aria-hidden="true"
                  />
                )}
                <Text size="sm" fw={700}>
                  {item.label}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
